<template>
  <div>
    <fieldset
      class="margin-top-0 margin-x-0 margin-bottom-md padding-0"
      style="border: 0"
    >
      <legend class="sr-only">Cancellation reason</legend>

      <ul
        class="margin-bottom-0"
        style="--list-style-type: none; --list-padding-left: 0"
      >
        <li
          v-for="(reason, index) in reasons"
          :key="reason.id || `fallback-${index}`"
        >
          <input
            :id="`cancel-reason-${index}`"
            v-model="selectedReason"
            type="radio"
            name="cancel-reason"
            class="sr-only with-icon"
            :value="reason"
            :disabled="isProcessing"
          />
          <label
            :for="`cancel-reason-${index}`"
            class="display-flex flex-items-center padding-vertical-3xs"
            style="cursor: var(--input-cursor); gap: var(--spacing-md)"
          >
            <font-awesome-icon
              v-if="isSelected(reason)"
              :icon="$v7.duotone.faDotCircle"
              size="lg"
              style="color: var(--fa-navy); flex-shrink: 0"
            />
            <font-awesome-icon
              v-else
              :icon="$v7.regular.faCircle"
              size="lg"
              style="color: var(--fa-gravy); flex-shrink: 0"
            />
            <span>{{ reason.label }}</span>
          </label>
        </li>
      </ul>
    </fieldset>

    <div
      v-if="selectedReason"
      class="margin-bottom-lg"
    >
      <div class="display-flex flex-content-between flex-items-baseline margin-bottom-sm">
        <label :for="commentFieldId">
          {{ commentFieldPrompt }}
          <span
            v-if="commentIsRequired"
            aria-hidden="true"
            >*</span
          >
        </label>
        <span
          class="size-sm muted"
          aria-live="polite"
        >
          {{ commentCounterLabel }}
        </span>
      </div>
      <textarea
        :id="commentFieldId"
        v-model="comment"
        class="width-full"
        rows="4"
        :maxlength="commentMaxLength"
        :disabled="isProcessing"
        :aria-required="commentIsRequired"
      ></textarea>
    </div>

    <div
      v-if="cancelError"
      class="card padding-md margin-top-lg"
      role="alert"
    >
      <p class="size-sm margin-bottom-0">Something went wrong while canceling. Please try again.</p>
    </div>
  </div>
</template>

<script>
  import { fetch } from 'app/utils/data'
  import { default as deserialize } from 'app/utils/deserialize'
  import { COMMENT_MAX_LENGTH, COMMENT_REQUIRED_LABELS, FALLBACK_REASONS } from 'app/components-wa/modals/cancel-subscription/cancellationReasons'

  export default {
    name: 'ScreenWhyAreYouCanceling',

    props: {
      isProcessing: {
        type: Boolean,
        default: false
      },
      cancelError: {
        type: Error,
        default: null
      }
    },

    emits: ['submit-readiness-changed'],

    data() {
      return {
        reasons: [],
        selectedReason: null,
        comment: '',
        commentMaxLength: COMMENT_MAX_LENGTH
      }
    },

    computed: {
      commentFieldId() {
        return 'cancel-reason-comment'
      },

      commentIsRequired() {
        if (!this.selectedReason) return false
        if (typeof this.selectedReason.requires_comment === 'boolean') {
          return this.selectedReason.requires_comment
        }
        return COMMENT_REQUIRED_LABELS.has(this.selectedReason.label)
      },

      commentFieldPrompt() {
        return this.commentIsRequired ? 'Tell us more.' : 'Anything to add?'
      },

      commentCounterLabel() {
        const used = this.comment.length
        return `${used} / ${this.commentMaxLength}`
      },

      submitPayload() {
        if (!this.selectedReason) return null
        const trimmedComment = this.comment.trim()
        if (this.commentIsRequired && trimmedComment.length === 0) return null

        return {
          reasonId: this.selectedReason.id || null,
          comment: trimmedComment || null
        }
      }
    },

    watch: {
      submitPayload: {
        immediate: true,
        handler(payload) {
          this.$emit('submit-readiness-changed', payload !== null)
        }
      }
    },

    mounted() {
      this.loadReasons()
    },

    methods: {
      async loadReasons() {
        try {
          const response = await fetch({ url: '/api/cancellation-reasons' })
          const data = await deserialize(response.data)
          // deserialize unwraps JSON-API: data.reasons is the array of
          // { id, label, requires_comment }. Order (with Other last) is
          // already enforced server-side; we render in the order received.
          this.reasons = Array.isArray(data.reasons) ? data.reasons : []
          if (this.reasons.length === 0) {
            this.useFallback()
          }
        } catch (e) {
          this.useFallback()
        }
      },

      useFallback() {
        this.reasons = FALLBACK_REASONS
      },

      isSelected(reason) {
        if (!this.selectedReason) return false
        // Identity comparison works because v-model assigns the same object
        // reference from the reasons array. Defensive fallback by id+label in
        // case future code paths re-create the reason objects.
        if (this.selectedReason === reason) return true
        if (reason.id && this.selectedReason.id === reason.id) return true
        return false
      },

      // Called by the parent modal when the user clicks the primary Cancel
      // Subscription action. Returns null if the form isn't ready to submit
      // (no reason picked, or required comment is empty). Otherwise returns
      // { reasonId, comment }.
      collectPayload() {
        return this.submitPayload
      }
    }
  }
</script>
